import React from "react";
import { Modal, Button } from "antd";
import { resolveFileUrl } from "../../../utils/safeUrl";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import VideoPlayer from "../../../Components/VideoPlayer";

export type TareaItem =
  | string
  | {
      id?: number;
      descripcion?: string;
      imagenes?: Array<{
        url?: string;
        thumbUrl?: string;
        /** Versión liviana (480p) del video; `null` si no existe. */
        previewUrl?: string | null;
        kind?: string;
        originalName?: string;
        comentario?: string | null;
      }>;
    };

export interface TareasModalRecord {
  propiedad: string;
  tareas: TareaItem[];
}

interface TareasViewModalProps {
  open: boolean;
  record: TareasModalRecord | null;
  onClose: () => void;
}

const TareasViewModal: React.FC<TareasViewModalProps> = ({
  open,
  record,
  onClose,
}) => {
  const tareas = record?.tareas ?? [];

  return (
    <Modal
      open={open}
      title={record?.propiedad ? `Tareas en ${formatPropertyName(record.propiedad)}` : "Tareas"}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Cerrar</Button>}
      width={720}
      destroyOnClose
    >
      {tareas.length === 0 ? (
        <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
          Sin tareas pendientes en esta propiedad.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {tareas.map((t, i) => {
            const desc = typeof t === "string" ? t : t?.descripcion ?? "";
            const imgs =
              typeof t === "string"
                ? []
                : Array.isArray(t?.imagenes)
                  ? t.imagenes
                  : [];
            return (
              <div
                key={(typeof t === "object" && t?.id) || i}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: 12,
                  background: "#fafafa",
                }}
              >
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    marginBottom: imgs.length ? 12 : 0,
                  }}
                >
                  {desc || (
                    <span style={{ color: "#999" }}>(Sin descripción)</span>
                  )}
                </div>
                {imgs.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    {imgs.map((img, j) => {
                      const isVideo =
                        String(img?.kind ?? "").toLowerCase() === "video";
                      // resolveFileUrl mapea cualquier URL no-http(s)/relativa a
                      // about:blank → corta `javascript:`/`data:` si una entrada
                      // de DB quedó corrupta. Round 2026-04-26 (P1-I).
                      const url = resolveFileUrl(img?.url);
                      const thumb = resolveFileUrl(img?.thumbUrl || img?.url);
                      if (!img?.url) return null;
                      if (isVideo) {
                        // La versión liviana pasa por el mismo saneo; si queda inválida
                        // (about:blank) se reproduce el original, sin selector.
                        const liviana = img?.previewUrl
                          ? resolveFileUrl(img.previewUrl)
                          : null;
                        return (
                          <VideoPlayer
                            key={j}
                            src={url}
                            previewSrc={liviana && liviana !== "about:blank" ? liviana : null}
                            wrapperStyle={{ width: 200 }}
                            nombreArchivo={img?.originalName}
                          />
                        );
                      }
                      return (
                        <a
                          key={j}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={img?.originalName || "Ver original"}
                        >
                          <img
                            src={thumb}
                            alt={img?.originalName || `Adjunto ${j + 1}`}
                            style={{
                              width: 120,
                              height: 120,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #e5e7eb",
                            }}
                          />
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default TareasViewModal;
